import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { History, Search, ShoppingBag, Utensils, X } from 'lucide-react';
import type { PublicPagePayload } from '@/hooks/storefront/useStorefrontApi';
import { isRestaurantStorefront, isTableServiceEnabled } from '@/lib/storefrontFulfillment';
import { cartCount, selectCartLines, useStorefrontCart } from '@/stores/storefrontCart';
import { StoreTableContext } from './StoreTableBanner';
import { storefrontOpeningStatus, type OpeningHours } from '@/lib/storefrontOpeningStatus';

interface StorefrontHeaderProps {
  page: PublicPagePayload | null;
  basePath: string;
  isSearchOpen: boolean;
  setIsSearchOpen: (open: boolean) => void;
  searchInput: string;
  setSearchInput: (value: string) => void;
}

export function StorefrontHeader({
  page,
  basePath,
  isSearchOpen,
  setIsSearchOpen,
  searchInput,
  setSearchInput,
}: StorefrontHeaderProps) {
  const { t } = useTranslation('storefront');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const carts = useStorefrontCart((store) => store.carts);
  const count = cartCount(selectCartLines(carts, page?.id));
  const restaurantMode = isRestaurantStorefront(page);
  const reservationMode = page?.mode === 'reservation';
  const [clock, setClock] = useState(() => new Date());
  const openingStatus = useMemo(
    () =>
      storefrontOpeningStatus(
        page?.opening_hours ?? (page?.settings?.opening_hours as OpeningHours | undefined),
        page?.shop?.timezone,
        clock,
      ),
    [clock, page?.opening_hours, page?.settings, page?.shop?.timezone],
  );

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Ecouteur raccourci clavier Command+K / Ctrl+K pour la recherche
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        window.requestAnimationFrame(() => searchInputRef.current?.focus());
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setIsSearchOpen]);

  const initialLetter = (page?.name ?? 'V').charAt(0).toUpperCase();

  return (
    <header
      className={`sticky top-0 z-30 w-full pt-[env(safe-area-inset-top,0px)] backdrop-blur-xl transition-colors ${
        restaurantMode
          ? 'border-b border-[var(--store-border)] bg-[var(--store-surface)]/90 text-[var(--store-ink)]'
          : 'border-b border-slate-200/80 bg-white/85 dark:border-charcoal-800/80 dark:bg-charcoal-900/85'
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-2 px-3 sm:px-6">
        {/* ZONES GAUCHE: Brand Logo + Titre + Badges d'état */}
        {!isSearchOpen && (
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
            <Link
              to={basePath}
              aria-label={t('back_to_shop')}
              className="group flex min-w-0 items-center gap-3 rounded-xl p-1 transition-all hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]"
            >
              {page?.logo_url ? (
                <img
                  src={page.logo_url}
                  alt=""
                  className={`size-10 shrink-0 rounded-full object-cover ${
                    restaurantMode
                      ? 'border border-[var(--store-border)] shadow-xs'
                      : 'shadow-2xs ring-2 ring-white/60 dark:ring-charcoal-800'
                  }`}
                />
              ) : (
                <span
                  aria-hidden
                  className="flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-2xs"
                  style={{
                    backgroundColor: 'var(--store-accent)',
                    color: 'var(--store-accent-foreground)',
                  }}
                >
                  {initialLetter}
                </span>
              )}

              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p
                    className={`truncate font-bold tracking-tight ${
                      restaurantMode
                        ? 'font-restaurant-display text-lg text-[var(--store-ink)]'
                        : 'text-sm text-charcoal-950 dark:text-white'
                    }`}
                  >
                    {page?.name ?? 'Velko Store'}
                  </p>

                  {openingStatus && (
                    <span
                      className={`hidden items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold sm:inline-flex ${
                        openingStatus.isOpen
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                          : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-300 dark:border-slate-700'
                      }`}
                    >
                      <span
                        className={`size-1.5 rounded-full ${
                          openingStatus.isOpen
                            ? 'bg-emerald-500 dark:bg-emerald-400'
                            : 'bg-slate-400'
                        }`}
                      />
                      {openingStatus.label}
                    </span>
                  )}
                </div>

                {page?.shop?.name && page.shop.name !== page.name && (
                  <p className="truncate text-[11px] font-medium text-[var(--store-ink-muted)]">
                    {page.shop.name}
                  </p>
                )}
              </div>
            </Link>

            {/* Context Pill Table (Mode Restaurant) */}
            {isTableServiceEnabled(page) && (
              <div className="hidden min-w-0 items-center md:flex">
                <StoreTableContext variant="chip" />
              </div>
            )}
          </div>
        )}

        {/* ZONE CENTRALE : Barre de recherche Desktop (Mode Standard & Restaurant) */}
        {!isSearchOpen && (
          <div className="hidden flex-1 max-w-md items-center px-4 md:flex">
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(true);
                window.requestAnimationFrame(() => searchInputRef.current?.focus());
              }}
              className={`flex h-11 w-full items-center justify-between rounded-full px-4 text-xs font-medium transition ${
                restaurantMode
                  ? 'border border-[var(--store-border)] bg-[var(--store-surface-low)] text-[var(--store-ink-muted)] hover:border-[var(--store-accent)] hover:text-[var(--store-ink)] shadow-xs'
                  : 'border border-slate-200/90 bg-slate-100/70 text-slate-500 hover:border-[var(--store-accent)] hover:bg-white dark:border-charcoal-700/80 dark:bg-charcoal-800/60 dark:text-slate-400'
              }`}
            >
              <span className="flex items-center gap-2 truncate">
                <Search
                  className={`size-3.5 ${restaurantMode ? 'text-[var(--store-accent)]' : 'text-slate-400'}`}
                  aria-hidden
                />
                <span>{t('search_products') || 'Rechercher un produit'}</span>
              </span>
              <kbd
                className={`hidden rounded-md px-1.5 py-0.5 font-mono text-[10px] font-semibold lg:inline-block ${
                  restaurantMode
                    ? 'border border-[var(--store-border)] bg-[var(--store-surface)] text-[var(--store-ink-muted)]'
                    : 'border border-slate-200 bg-white text-slate-400 dark:border-charcoal-700 dark:bg-charcoal-900'
                }`}
              >
                ⌘K
              </kbd>
            </button>
          </div>
        )}

        {/* MODE RECHERCHE ACTIVE (Plein écran ou expand) */}
        {isSearchOpen && (
          <div className="relative flex w-full items-center animate-slide-in-right">
            <Search className="absolute left-3.5 size-4 text-[var(--store-accent)]" aria-hidden />
            <input
              ref={searchInputRef}
              autoFocus
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={
                restaurantMode
                  ? 'Rechercher un plat, un menu, une boisson...'
                  : t('search_products') || 'Rechercher un produit...'
              }
              className={`h-11 w-full rounded-full border pl-10 pr-12 text-sm font-medium shadow-xs outline-none transition focus:ring-2 ${
                restaurantMode
                  ? 'border-[var(--store-accent)] bg-[var(--store-surface)] text-[var(--store-ink)] focus:ring-[var(--store-accent)]/30'
                  : 'border-[var(--store-accent)]/40 bg-slate-50/90 text-charcoal-950 focus:ring-[var(--store-accent)]/30 dark:bg-charcoal-950 dark:text-white'
              }`}
            />
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(false);
                setSearchInput('');
              }}
              aria-label={t('clear_search')}
              className="absolute right-0 flex size-11 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        )}

        {/* ZONES DROITE : Actions Rapides & Contextuelles */}
        {!isSearchOpen && (
          <div className="flex shrink-0 items-center gap-2">
            {/* BOUTON LOUPE MOBILE */}
            <button
              type="button"
              onClick={() => {
                setIsSearchOpen(true);
                window.requestAnimationFrame(() => searchInputRef.current?.focus());
              }}
              aria-label={t('search_products')}
              className={`flex size-11 items-center justify-center rounded-full transition md:hidden ${
                restaurantMode
                  ? 'bg-[var(--store-surface-low)] text-[var(--store-ink)] hover:bg-[var(--store-surface)]'
                  : 'bg-slate-100 text-charcoal-950 hover:bg-slate-200 dark:bg-charcoal-800 dark:text-white'
              }`}
            >
              <Search className="size-4" aria-hidden />
            </button>

            {/* SUIVI DE COMMANDE / HISTORIQUE */}
            <Link
              to={`${basePath}/${reservationMode ? 'mes-reservations' : 'mes-commandes'}`}
              aria-label={reservationMode ? t('reservations_short') : t('orders_short')}
              title={reservationMode ? t('reservations_short') : 'Mes commandes'}
              className={`hidden size-11 items-center justify-center rounded-full transition sm:flex ${
                restaurantMode
                  ? 'bg-[var(--store-surface-low)] text-[var(--store-ink-muted)] hover:bg-[var(--store-surface)] hover:text-[var(--store-accent)]'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-charcoal-800 dark:text-slate-300'
              }`}
            >
              <History className="size-4" aria-hidden />
            </Link>

            {/* BOUTON PANIER / SELECTION (Desktop Header) */}
            <Link
              to={`${basePath}/${reservationMode ? 'reservation' : 'panier'}`}
              aria-label={reservationMode ? t('view_selection') : t('view_cart')}
              className={`relative inline-flex min-h-11 items-center gap-2 rounded-full px-4 text-xs font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)] ${
                restaurantMode
                  ? 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)] hover:opacity-90 shadow-sm'
                  : 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)] hover:opacity-95 shadow-xs'
              }`}
            >
              {reservationMode ? (
                <Utensils className="size-4" aria-hidden />
              ) : (
                <ShoppingBag className="size-4" aria-hidden />
              )}
              <span className="hidden sm:inline">
                {reservationMode ? t('selection_short') : t('cart_short')}
              </span>
              {count > 0 && (
                <span className="flex size-5 items-center justify-center rounded-full bg-black/20 text-[11px] font-extrabold leading-none">
                  {count}
                </span>
              )}
            </Link>
          </div>
        )}
      </div>

      {/* MOBILE RESTAURANT TABLE CHIP BAR (Rappel de Table sous le header mobile) */}
      {isTableServiceEnabled(page) && !isSearchOpen && (
        <div className="border-t border-[var(--store-border)] bg-[var(--store-surface)]/90 px-3 py-1.5 text-[var(--store-ink)] md:hidden">
          <div className="mx-auto flex max-w-6xl items-center justify-between">
            <StoreTableContext variant="chip" />
            <span className="text-[11px] font-semibold text-[var(--store-ink-muted)]">
              Service en salle actif
            </span>
          </div>
        </div>
      )}
    </header>
  );
}
